import React, { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Box, CircularProgress, Avatar, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { getLatestAssignmentsByStudent } from '../../services/assignmentService';
import { useAuth } from '../AuthContext';
import { Assignment } from '../../types/interfaces';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReviewsIcon from '@mui/icons-material/Reviews';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import StudyCoachCard from './StudyCoachCard';

const AssignmentCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const AssignmentDetails = styled(Box)({
  marginLeft: '20px',
  flex: 1,
  minWidth: 0, // Allow text to truncate if needed
});



// Helper function to get status icon and color
const getStatusInfo = (status: string) => {
  switch (status) {
    case 'Completed':
      return { 
        icon: <CheckCircleIcon />, 
        color: 'success' as const, 
        label: 'Valmis',
        chipColor: '#4caf50'
      };
    case 'Reviewed':
      return { 
        icon: <VisibilityIcon />, 
        color: 'info' as const, 
        label: 'Tarkastettu',
        chipColor: '#2196f3'
      };
    case 'InReview':
      return { 
        icon: <ReviewsIcon />, 
        color: 'warning' as const, 
        label: 'Tarkastuksessa',
        chipColor: '#ff9800'
      };
    case 'Active':
      return { 
        icon: <PlayCircleOutlineIcon />, 
        color: 'primary' as const, 
        label: 'Aktiivinen',
        chipColor: '#1976d2'
      };
    case 'Pending':
      return { 
        icon: <RateReviewIcon />, 
        color: 'info' as const, 
        label: 'Odottaa tarkistusta',
        chipColor: '#17a2b8'
      };
    case 'Overdue':
      return { 
        icon: <WarningIcon />, 
        color: 'error' as const, 
        label: 'Myöhässä',
        chipColor: '#f44336'
      };
    case 'New':
      return { 
        icon: <FiberNewIcon />, 
        color: 'secondary' as const, 
        label: 'Uusi',
        chipColor: '#9c27b0'
      };
    default:
      return { 
        icon: <HourglassEmptyIcon />, 
        color: 'default' as const, 
        label: status,
        chipColor: '#757575'
      };
  }
};

const AssignmentCardContent: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  const navigate = useNavigate();
  const statusInfo = getStatusInfo(assignment.status);

  const handleCardClick = () => {
    navigate(`/assignment/${assignment.id}`);
  };

  return (
    <AssignmentCard onClick={handleCardClick}>
      <Avatar 
        sx={{ 
          bgcolor: 'primary.main',
          width: 48,
          height: 48,
          boxShadow: 2
        }}
      >
        <SchoolIcon fontSize="medium" />
      </Avatar>
      <AssignmentDetails>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              pr: 1,
              fontWeight: 'medium',
              color: 'text.primary',
              lineHeight: 1.3
            }}
          >
            {assignment.exercise?.order}. {assignment.exercise?.instructions}
          </Typography>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            size="small"
            sx={{ 
              backgroundColor: statusInfo.chipColor,
              color: 'white',
              fontWeight: 'medium',
              '& .MuiChip-icon': {
                color: 'white'
              },
              '& .MuiChip-label': {
                fontSize: '0.75rem'
              }
            }}
          />
        </Box>
        <Typography 
          variant="body2" 
          color="textSecondary" 
          sx={{ 
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem'
          }}
        >
          <EventIcon fontSize="small" sx={{ marginRight: 0.5 }} />
          Oppitunti: {assignment.exercise?.lesson.title}
        </Typography>
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: 'normal'
          }}
        >
          Eräpäivä: {new Date(assignment.dueDate ?? new Date()).toLocaleDateString('fi-FI')}
        </Typography>
      </AssignmentDetails>
    </AssignmentCard>
  );
};

const LatestAssignments: React.FC = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(async () => {
    if (user) {
      try {
        const response = await getLatestAssignmentsByStudent(user.email);
        console.log('Assignments response:', response.data);
        // Log first assignment structure
        if (response.data.length > 0) {
          console.log('First assignment:', response.data[0]);
          console.log('Exercise:', response.data[0].exercise);
          console.log('Lesson:', response.data[0].exercise?.lesson);
        }
        setAssignments(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch assignments');
        console.error('Failed to fetch assignments:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  if (loading) {
    return (
      <Box mt={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={3}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  // Group assignments by course
  const assignmentsByCourse = assignments.reduce((acc, assignment) => {
    const lesson = assignment.exercise?.lesson as any;
    const courseTitle = lesson?.course?.name || 'Ei kurssia';
    const courseId = lesson?.course?.id || 0;
    const key = `${courseId}-${courseTitle}`;
    
    if (!acc[key]) {
      acc[key] = {
        courseTitle,
        courseId,
        assignments: []
      };
    }
    acc[key].assignments.push(assignment);
    return acc;
  }, {} as Record<string, { courseTitle: string; courseId: number; assignments: Assignment[] }>);

  // Sort courses by title and assignments within each course by exercise order
  const sortedCourses = Object.values(assignmentsByCourse)
    .sort((a, b) => a.courseTitle.localeCompare(b.courseTitle))
    .map(course => ({
      ...course,
      assignments: course.assignments.sort((a, b) => {
        // Sort by lesson order first, then by exercise order within the lesson
        const lessonOrderA = (a.exercise?.lesson as any)?.order || 0;
        const lessonOrderB = (b.exercise?.lesson as any)?.order || 0;
        if (lessonOrderA !== lessonOrderB) {
          return lessonOrderA - lessonOrderB;
        }
        // If same lesson, sort by exercise order
        return (a.exercise?.order || 0) - (b.exercise?.order || 0);
      })
    }));

  return (
    <Box mt={3}>
      <StudyCoachCard assignments={assignments} studentName={user?.name} />

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 3 }}>
        📚 Tehtäväsi kursseittain
      </Typography>

      {sortedCourses.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Ei tehtäviä löytynyt.
        </Typography>
      ) : (
        sortedCourses.map((course, index) => (
          <Accordion 
            key={`${course.courseId}-${index}`}
            defaultExpanded={index === 0}
            sx={{ 
              mb: 2,
              '&:before': { display: 'none' },
              boxShadow: 2,
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 2
                }
              }}
            >
              <SchoolIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {course.courseTitle}
              </Typography>
              <Chip 
                label={`${course.assignments.length} tehtävä${course.assignments.length !== 1 ? 'ä' : ''}`}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'inherit',
                  fontWeight: 'medium'
                }}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              {course.assignments.map((assignment) => (
                <AssignmentCardContent key={assignment.id} assignment={assignment} />
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default LatestAssignments;
